import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CaretDown, User as UserIcon, CheckCircle, ArrowsClockwise } from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("de-DE").format(Math.round(n || 0));

export default function MultiCharSwitcher() {
  const { user, refresh } = useAuth();
  const [chars, setChars] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user?.linked_esx_identifier && !user?.linked_base_hash) { setChars([]); return; }
    api.get("/character/mine").then(r => setChars(r.data)).catch(() => {});
  }, [user?.linked_esx_identifier, user?.character_id]);

  if (chars.length <= 1) return null;

  const active = chars.find(c => c.active) || chars[0];

  const switchTo = async (charId, name) => {
    if (charId === active.id) { setOpen(false); return; }
    setBusy(true);
    try {
      await api.post(`/character/switch/${charId}`);
      toast.success(`Gewechselt zu ${name}`);
      await refresh();
      setOpen(false);
      // Reload to refresh all pages with new char data
      window.location.reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wechsel fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <div className="border-t border-tactical-border" data-testid="multichar-switcher">
      <button
        data-testid="multichar-toggle"
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-2.5 flex items-center justify-between gap-2 hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-2 min-w-0">
          <ArrowsClockwise size={14} className="text-volt flex-shrink-0" />
          <div className="text-left min-w-0">
            <div className="font-mono text-[9px] text-white/40 uppercase tracking-widest">Char {active.slot}</div>
            <div className="text-xs text-white truncate">{active.first_name} {active.last_name}</div>
          </div>
        </div>
        <CaretDown size={12} className={`text-white/50 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="bg-black/60 border-t border-tactical-border/50 max-h-64 overflow-y-auto">
          {chars.map(c => (
            <button
              key={c.id}
              data-testid={`char-switch-${c.slot}`}
              disabled={busy}
              onClick={() => switchTo(c.id, `${c.first_name} ${c.last_name}`)}
              className={`w-full px-4 py-2 flex items-center gap-2 text-left transition-colors ${
                c.active ? "bg-volt/10 border-l-2 border-volt" : "border-l-2 border-transparent hover:bg-white/5"
              } disabled:opacity-50`}
            >
              <UserIcon size={14} className={c.active ? "text-volt" : "text-white/40"} />
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white truncate">{c.first_name} {c.last_name}</div>
                <div className="font-mono text-[9px] text-white/40 truncate">
                  Char {c.slot} · {c.job_grade_label} · ${fmt((c.cash || 0) + (c.bank || 0))}
                </div>
              </div>
              {c.active && <CheckCircle size={12} className="text-volt flex-shrink-0" weight="fill" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
